import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Data Restrictions',
  content: `
Data restrictions control **which data** someone can see in Pliable. They're separate from [Roles](/docs/roles), which control what someone can **do**. A restriction never gives anyone more access. It only narrows what they'd otherwise see.

This page explains how restrictions work wherever they come from. To set them up for people in your organization, see [Security & Governance](/docs/security-and-governance). To set them for embedded dashboards or API tokens, see [Embedding Pliable](/docs/embedding-pliable) and the [Pliable API](/docs/pliable-api).

## Two kinds of restriction

| | What it limits | Example |
|---|---|---|
| **Row restriction** | Which rows are counted in a metric. | "Only rows where Store is 301", "Only rows where Employee Type is not Executive" |
| **Metric visibility** | Whether a metric can be seen at all. | Hide "Bonus Pool" |

## Where restrictions come from

Someone's restrictions are everything that applies to them:

- **Their group**, set in Security & Governance.
- **Their own user record**, set in Security & Governance on top of their group's rules.
- **Dimension filters** on their user record. This is the older per-user setting, which still works.
- **An embedded session or API token.** The \`data_scope\` you send when you create it applies for its whole lifetime.

All of these combine with **AND**. If a group says "Region is North" and the person's own record says "Employee Type is not Executive", they see rows that match both.

## How a row restriction is applied

A row restriction is applied **per metric**, not to a whole chart at once. For each metric in a chart, dashboard or answer, Pliable asks two questions.

**1. Does the restriction apply to this metric?**
A restriction applies either to **all metrics**, or only to the **specific metrics** it names. For example, "Employee Type is not Executive" might apply only to *Total Pay*. Then *Eggs Sold* still counts everyone.

**2. Is the metric connected to the restricted field?**
A metric is *connected* when its data can be traced to the field being restricted, such as each timecard being linked to an employee. A connected metric is filtered, so only the allowed rows are counted.

When a restriction applies to all metrics, some metrics may not be connected to the restricted field. For example, *Eggs Sold* isn't recorded per employee. What happens to those depends on the restriction:

- **Hide them** (the default). The metric isn't shown, and anything that asks for it gets an error rather than a number the restriction doesn't cover.
- **Show them.** The metric is shown in full. Choose this only when the metric really doesn't contain the restricted information, such as a company-wide total.

A restriction that names specific metrics only accepts metrics that are connected. Pliable refuses to save, or to create the session or token, if a named metric isn't connected.

### Calculated metrics

A calculated metric, such as *Margin = Revenue − Cost*, is restricted through the metrics it's built from. Naming a calculated metric in a restriction restricts all its parts. If a restriction covers some parts but not others, the result can be misleading, so Security & Governance warns you about it.

### Hiding a metric hides what's built from it

Hiding a metric also hides every metric calculated from it. Otherwise the hidden number could be worked out by subtraction.

## Where restrictions are enforced

Restrictions are applied by Pliable's servers when the data is fetched, not by the screen showing it. They apply everywhere data comes out of Pliable:

- Charts and dashboards, including embedded ones.
- Ellie, including the [Ellie APIs](/docs/ellie-invoke-api) and the [MCP Server](/docs/mcp-server).
- The Pliable API and downloads.
- Filter dropdowns, which only list values the person is allowed to see.

Ellie is told about someone's restrictions so it can explain them, but it can't change them. Nothing Ellie writes affects which rows are returned.

Filters on a dashboard or chart can narrow the data further, but they can never widen a restriction.

## Pliable fails closed

When Pliable can't apply a restriction with certainty, it returns an error rather than unrestricted data. For example:

- The restriction points at a field or metric that has been deleted.
- A metric isn't connected to the restricted field, and the restriction is set to hide such metrics.
- A breakdown would list values from data that's related to the restricted data. For instance, breaking a metric the restriction doesn't cover down by a field linked to the restricted one.

**Goals** are not shown to anyone with restrictions or hidden metrics, because a goal can reveal both a number and the slice of data it's set for.

## Things to keep in mind

- **Restrictions narrow; roles decide.** Admins and Builders can read raw data and change the data model, so restrictions don't fully contain them. Give anyone who must be restricted the **ReadOnly** role, or **ReadOnly_Partner** for external users.
- **A restriction on specific metrics leaves the rest unrestricted.** This matters most for embedding and API tokens. A session or token must include at least one restriction, but if that restriction names only *Total Pay*, every other metric is unrestricted. To cover everything, add a restriction that applies to all metrics, or hide the metrics that shouldn't be seen.
- **Restrictions use published metrics and fields.** Changes made in Develop don't loosen a restriction until they're published, and the publish step lists any changes to what restricted people can see. See *Making changes to your data model* in [Security & Governance](/docs/security-and-governance).
- **You can check the result.** In Security & Governance, **Preview access** shows dashboards, charts and Ellie exactly as a chosen person or group sees them.
`,
};

export default doc;
